/**
 * DocumentList Component
 *
 * Display uploaded documents for an applicant with:
 * - Document type, status, upload date
 * - OCR confidence score
 * - Extracted data (name, DOB, document number)
 * - Fraud signals if any
 * - Download via presigned URL
 * - Delete with confirmation
 */

import React, { useState, useCallback } from 'react';
import {
  FileText,
  Image,
  Download,
  Trash2,
  Eye,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Shield
} from 'lucide-react';
import { useApplicantDocuments, useDeleteDocument, useDownloadDocument, useAnalyzeDocument, useReprocessDocument } from '../hooks/useDocuments';
import { useToast } from '../contexts/ToastContext';
import { ConfirmDialog } from './shared';
import { Button, Badge } from './ui';
import { cn } from '../lib/utils';

// Status configuration with Badge variants
const STATUS_CONFIG = {
  pending: { label: 'Pending', variant: 'secondary', icon: Clock },
  processing: { label: 'Processing', variant: 'info', icon: Loader2, animate: true },
  verified: { label: 'Verified', variant: 'success', icon: CheckCircle2 },
  failed: { label: 'Failed', variant: 'destructive', icon: XCircle },
  rejected: { label: 'Rejected', variant: 'destructive', icon: XCircle },
  pending_review: { label: 'Review', variant: 'warning', icon: AlertTriangle },
};

// Document type labels
const DOCUMENT_TYPE_LABELS = {
  passport: 'Passport',
  driver_license: "Driver's License",
  id_card: 'ID Card',
  utility_bill: 'Proof of Address',
  bank_statement: 'Bank Statement',
  proof_of_address: 'Proof of Address',
  government_letter: 'Government Letter',
  tax_document: 'Tax Document',
  selfie: 'Selfie',
  other: 'Other Document',
};

/**
 * Format date for display
 */
function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format file size
 */
function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get icon for file type
 */
function FileIcon({ type, contentType }) {
  if (contentType?.startsWith('image/') || ['passport', 'driver_license', 'id_card', 'selfie'].includes(type)) {
    return <Image size={20} />;
  }
  return <FileText size={20} />;
}

/**
 * Single document item component
 */
function DocumentItem({ document, onView, onDownload, onDelete, onReanalyze, onReprocess, isDeleting, isDownloading, isReanalyzing, isReprocessing }) {
  const [expanded, setExpanded] = useState(false);

  const status = STATUS_CONFIG[document.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;
  const typeLabel = DOCUMENT_TYPE_LABELS[document.document_type] || document.document_type;

  // Check for fraud signals
  const hasFraudSignals = document.fraud_signals && document.fraud_signals.length > 0;
  const hasSeriousFraud = hasFraudSignals && document.fraud_signals.some(s => s.severity !== 'info');
  const hasVerificationChecks = document.verification_checks && document.verification_checks.length > 0;
  const isVerified = document.status === 'verified';
  const hasExtractedData = document.extracted_data && Object.keys(document.extracted_data).length > 0;

  return (
    <div className={cn(
      'bg-grey-100 dark:bg-grey-800 rounded-xl overflow-hidden transition-all',
      'hover:bg-grey-200/50 dark:hover:bg-grey-700/50',
      expanded && 'bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-700'
    )}>
      <div
        className="flex items-center gap-3 p-3.5 cursor-pointer flex-wrap md:flex-nowrap"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="w-10 h-10 rounded-lg bg-white dark:bg-grey-800 flex items-center justify-center text-grey-500 flex-shrink-0">
          <FileIcon type={document.document_type} contentType={document.content_type} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{typeLabel}</div>
          <div className="text-xs text-grey-500 truncate">
            {document.file_name || document.filename || 'Unnamed document'} • {formatFileSize(document.file_size)}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0 order-5 w-full md:order-none md:w-auto mt-2 md:mt-0">
          {document.ocr_confidence !== null && document.ocr_confidence !== undefined && (
            <div className="flex items-center gap-1 px-2 py-1 bg-teal-500/15 rounded-md text-[11px] font-semibold text-teal-600 dark:text-teal-400">
              <Sparkles size={14} />
              <span>{Math.round(document.ocr_confidence)}% OCR</span>
            </div>
          )}

          {hasSeriousFraud ? (
            <Badge variant="destructive" data-testid="doc-integrity-fail" className="flex items-center gap-1">
              <XCircle size={14} />
              <span>Tampered</span>
            </Badge>
          ) : (isVerified || hasVerificationChecks) ? (
            <Badge variant="success" data-testid="doc-integrity-pass" className="flex items-center gap-1">
              <CheckCircle2 size={14} />
              <span>Clean</span>
            </Badge>
          ) : hasFraudSignals ? (
            <Badge variant="warning" className="flex items-center gap-1">
              <Shield size={14} />
              <span>{document.fraud_signals.length} signal{document.fraud_signals.length > 1 ? 's' : ''}</span>
            </Badge>
          ) : null}
        </div>

        <Badge variant={status.variant} className="flex items-center gap-1.5 flex-shrink-0">
          <StatusIcon size={14} className={status.animate ? 'animate-spin' : ''} />
          <span>{status.label}</span>
        </Badge>

        <div className="text-xs text-grey-500 flex-shrink-0 min-w-[130px] text-right hidden md:block">
          {formatDate(document.uploaded_at || document.created_at)}
        </div>

        <div className="flex gap-1 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); onView(document); }}
            title="Preview document"
          >
            <Eye size={16} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); onDownload(document); }}
            disabled={isDownloading}
            title="Download document"
          >
            {isDownloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          </Button>
          {document.status === 'processing' ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => { e.stopPropagation(); onReprocess(document); }}
              disabled={isReprocessing}
              title="Reprocess stuck document"
            >
              {isReprocessing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => { e.stopPropagation(); onReanalyze(document); }}
              disabled={isReanalyzing}
              title="Re-run AI analysis"
            >
              {isReanalyzing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="hover:bg-red-500/10 hover:text-red-500"
            onClick={(e) => { e.stopPropagation(); onDelete(document); }}
            disabled={isDeleting}
            title="Delete document"
          >
            {isDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
          </Button>
        </div>

        <div className="text-grey-500 flex-shrink-0">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-grey-200 dark:border-grey-700 pt-4">
          {/* Extracted data */}
          {hasExtractedData && (
            <div className="mb-4 last:mb-0">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-grey-500 mb-2.5">
                <Sparkles size={14} />
                Extracted Data
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {document.extracted_data.full_name && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[11px] text-grey-500 uppercase tracking-wide">Full Name</span>
                    <span className="text-[13px]">{document.extracted_data.full_name}</span>
                  </div>
                )}
                {document.extracted_data.date_of_birth && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[11px] text-grey-500 uppercase tracking-wide">Date of Birth</span>
                    <span className="text-[13px]">{document.extracted_data.date_of_birth}</span>
                  </div>
                )}
                {document.extracted_data.document_number && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[11px] text-grey-500 uppercase tracking-wide">Document Number</span>
                    <span className="text-[13px]">{document.extracted_data.document_number}</span>
                  </div>
                )}
                {document.extracted_data.expiry_date && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[11px] text-grey-500 uppercase tracking-wide">Expiry Date</span>
                    <span className="text-[13px]">{document.extracted_data.expiry_date}</span>
                  </div>
                )}
                {document.extracted_data.nationality && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[11px] text-grey-500 uppercase tracking-wide">Nationality</span>
                    <span className="text-[13px]">{document.extracted_data.nationality}</span>
                  </div>
                )}
                {document.extracted_data.issuing_country && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[11px] text-grey-500 uppercase tracking-wide">Issuing Country</span>
                    <span className="text-[13px]">{document.extracted_data.issuing_country}</span>
                  </div>
                )}
                {document.extracted_data.address && (
                  <div className="flex flex-col gap-0.5 col-span-2">
                    <span className="text-[11px] text-grey-500 uppercase tracking-wide">Address</span>
                    <span className="text-[13px]">
                      {typeof document.extracted_data.address === 'object'
                        ? [
                            document.extracted_data.address.street,
                            document.extracted_data.address.city,
                            document.extracted_data.address.state,
                            document.extracted_data.address.postal_code,
                            document.extracted_data.address.country
                          ].filter(Boolean).join(', ')
                        : document.extracted_data.address}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MRZ validation */}
          {document.mrz_valid !== undefined && (
            <div className="mb-4 last:mb-0">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-grey-500 mb-2.5">
                <Shield size={14} />
                MRZ Validation
              </div>
              <div className={cn(
                'flex items-center gap-2 p-2.5 rounded-lg text-[13px]',
                document.mrz_valid
                  ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                  : 'bg-red-500/10 text-red-600 dark:text-red-400'
              )}>
                {document.mrz_valid ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                <span>{document.mrz_valid ? 'MRZ checksums valid' : 'MRZ validation failed'}</span>
              </div>
            </div>
          )}

          {/* Fraud signals */}
          {hasFraudSignals && (
            <div className="mb-4 last:mb-0">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-500 mb-2.5">
                <AlertTriangle size={14} />
                Fraud Signals
              </div>
              <div className="flex flex-col gap-2">
                {document.fraud_signals.map((signal, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-lg text-[13px] text-amber-600 dark:text-amber-400">
                    <AlertTriangle size={14} />
                    <span>{signal.description || signal.type || signal}</span>
                    {signal.confidence && (
                      <span className="ml-auto font-semibold">{Math.round(signal.confidence * 100)}%</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI analysis */}
          {document.ai_analysis && (
            <div className="mb-4 last:mb-0">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-grey-500 mb-2.5">
                <Sparkles size={14} />
                AI Analysis
              </div>
              <div className="text-[13px] leading-relaxed text-grey-600 dark:text-grey-400 p-3 bg-grey-100 dark:bg-grey-800 rounded-lg">
                {document.ai_analysis.summary || document.ai_analysis.notes || JSON.stringify(document.ai_analysis)}
              </div>
            </div>
          )}

          {/* No details available */}
          {!hasExtractedData && !hasFraudSignals && !document.ai_analysis && document.mrz_valid === undefined && (
            <div className="text-center py-5 text-grey-500 text-[13px]">
              No additional details available
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Main DocumentList component
 */
export default function DocumentList({ applicantId, onDocumentClick, showEmpty = true }) {
  const toast = useToast();

  // State for delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Fetch documents
  const { data: documents, isLoading, error, refetch } = useApplicantDocuments(applicantId);

  // Mutations
  const deleteMutation = useDeleteDocument();
  const downloadMutation = useDownloadDocument();
  const analyzeMutation = useAnalyzeDocument();
  const reprocessMutation = useReprocessDocument();

  // Handle document view
  const handleView = useCallback((doc) => {
    onDocumentClick?.(doc);
  }, [onDocumentClick]);

  // Handle document download
  const handleDownload = useCallback(async (doc) => {
    try {
      await downloadMutation.mutateAsync({
        id: doc.id,
        openInNewTab: true,
        filename: doc.file_name || doc.filename,
      });
    } catch (err) {
      toast.error(`Failed to download: ${err.message}`);
    }
  }, [downloadMutation, toast]);

  // Handle delete confirmation
  const handleDeleteClick = useCallback((doc) => {
    setDeleteTarget(doc);
  }, []);

  // Execute delete
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;

    try {
      await deleteMutation.mutateAsync({
        id: deleteTarget.id,
        applicantId,
      });
      toast.success('Document deleted successfully');
    } catch (err) {
      toast.error(`Failed to delete: ${err.message}`);
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteMutation, applicantId, toast]);

  // Handle re-analyze
  const handleReanalyze = useCallback(async (doc) => {
    try {
      await analyzeMutation.mutateAsync({ id: doc.id });
      toast.success('AI analysis started');
    } catch (err) {
      toast.error(`Failed to start analysis: ${err.message}`);
    }
  }, [analyzeMutation, toast]);

  // Handle reprocess (for stuck documents)
  const handleReprocess = useCallback(async (doc) => {
    try {
      await reprocessMutation.mutateAsync({ id: doc.id, applicantId });
      toast.success('Document queued for reprocessing');
    } catch (err) {
      toast.error(`Failed to reprocess: ${err.message}`);
    }
  }, [reprocessMutation, applicantId, toast]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-grey-500 text-center">
        <Loader2 size={24} className="animate-spin" />
        <span>Loading documents...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-red-500 text-center">
        <AlertTriangle size={24} />
        <span>Failed to load documents</span>
        <Button variant="secondary" onClick={() => refetch()}>
          <RefreshCw size={14} className="mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  // Empty state
  const documentList = Array.isArray(documents) ? documents : documents?.items || [];

  if (documentList.length === 0 && showEmpty) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-grey-500 text-center">
        <FileText size={32} />
        <span>No documents uploaded yet</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {documentList.map((doc) => (
        <DocumentItem
          key={doc.id}
          document={doc}
          onView={handleView}
          onDownload={handleDownload}
          onDelete={handleDeleteClick}
          onReanalyze={handleReanalyze}
          onReprocess={handleReprocess}
          isDeleting={deleteMutation.isPending && deleteMutation.variables?.id === doc.id}
          isDownloading={downloadMutation.isPending && downloadMutation.variables?.id === doc.id}
          isReanalyzing={analyzeMutation.isPending && analyzeMutation.variables?.id === doc.id}
          isReprocessing={reprocessMutation.isPending && reprocessMutation.variables?.id === doc.id}
        />
      ))}

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Document"
        message={`Are you sure you want to delete "${deleteTarget?.file_name || deleteTarget?.filename || 'this document'}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
